import type { AiPreferences } from '@timelyapp/integration-cloud-api-spec';
import timelyAnalytics from 'tracking/timely-analytics';
import debug from 'utils/debug';

/**
 * Tracks notable changes to AI preferences for analytics purposes.
 * This function detects changes to enabled state and preset,
 * and triggers the appropriate analytics events.
 *
 * Unchanged event naming/payloads after move from tic.apiSlice.ts
 *
 * Do NOT re-add the `analytics.` filename prefix: the resulting
 * `/analytics/analytics.*` dev URL matches common tracker-blocker rules, and one
 * blocked module fails the whole ES module graph — the app never mounts (TT-5522).
 */
export const trackNotableAiPreferencesChange = ({
  previous,
  current,
}: {
  previous: AiPreferences;
  current: AiPreferences;
}): void => {
  // Create tracking properties from current state
  const trackingProperties = {
    enabled: !!current.enabled,
    preset: current.preset || 'unknown',
    train_tags: !!current.train_tags,
    suggest_note: !!current.suggest_note,
    show_timesheet_predictions: !!current.show_timesheet_predictions,
  };

  // Track enabled/disabled state changes
  const changedEnabled = previous.enabled !== current.enabled;
  if (changedEnabled) {
    const event = current.enabled ? 'TIC+AI Enabled' : 'TIC+AI Disabled';
    timelyAnalytics.trackTic(event, trackingProperties);
    debug.template.ai(`Tracked AI ${current.enabled ? 'enabled' : 'disabled'}`, trackingProperties);
  }

  // Track preset changes (only if AI is enabled)
  const changedPreset = previous.enabled && previous.preset !== current.preset;
  if (changedPreset) {
    const event = 'TIC+AI Changed Preset';
    timelyAnalytics.trackTic(event, trackingProperties);
    debug.template.ai(event, {
      from: previous.preset || 'unknown',
      to: current.preset || 'unknown',
      ...trackingProperties,
    });
  }
};
